import {
  Avatar,
  Button,
  Dropdown,
  Image,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";
import Header from "../../atoms/Header";
import TableUi from "../../atoms/Table";
import DashboardTemplate from "../../templates/Dashboard";
import {
  getInitials,
  PAGE_LIMIT,
  ROLE_TYPE,
  useStableRandomColors,
} from "../../config/utils";
import Filters from "./Components/Filters";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../config/hooks";
import { useNavigate } from "react-router-dom";
import { CaretDownOutlined, MoreOutlined } from "@ant-design/icons";
import { CrossIcon, DeleteIcon, TickIcon } from "../../atoms/CustomIcons";
import { AxiosError } from "axios";
import { ErrorMessage } from "../../atoms/Toasts/ErrorMessage";
import { PopUp } from "../../atoms/PopUp";
import ConfirmDelete from "../../atoms/ConfirmDelete";
import ChangePasswordPopUp from "../MyProfile/Components/ChangePasswordPopUp";
import EmptyCard from "../../atoms/EmptyCard";
import { UsersTableType } from "./ducks/types";
import { getUsersListing, removeUsersListingData } from "./ducks/actions";
import { changeUserStatus, deleteUser } from "./ducks/services";
import { SuccessMessage } from "../../atoms/Toasts/SuccessMessage";
import { PhoneInput } from "react-international-phone";
import { formatRelativeTime } from "../../atoms/TimeFormatDisplay";

export default function AdminTeam() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userListing = useAppSelector((state) => state?.team?.usersData);
  const myProfileData = useAppSelector(
    (state) => state?.myProfile?.userData?.data
  );
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [tableRecord, setTableRecord] = useState<UsersTableType | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isAllDeleteModalOpen, setIsAllDeleteModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isChangePswModalOpen, setIsChangePswModalOpen] = useState(false);
  const [isDeactivatedModalOpen, setIsDeactivatedModalOpen] = useState(false);
  const [deactivated, isDeactivated] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const avatarColor = useStableRandomColors(PAGE_LIMIT);

  const [filters, setFilters] = useState<{
    search_text: string | undefined;
    deactivated: boolean | undefined;
    company: string | undefined;
    position: string | undefined;
    language: string | undefined;
    status: string | undefined;
  }>({
    search_text: undefined,
    deactivated: false,
    company: undefined,
    position: undefined,
    language: undefined,
    status: undefined,
  });

  const fetchUserListing = () => {
    const payload = {
      type: ["client_manager", "super_admin"],
      status: ["active"],
      offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
      limit: limit,
    };
    dispatch(getUsersListing(payload));
  };

  const changeStatus = (val: string) => {
    if (val === "deactivated") {
      setIsDeactivatedModalOpen(true);
      return;
    }
    isDeactivated(false);
    setLoading(true);
    functionChangeStatus(val);
  };

  const functionChangeStatus = (val: string) => {
    const payload = {
      status: val,
    };
    changeUserStatus(tableRecord?.id || "", payload)
      .then(() => {
        setLoading(false);
        fetchUserListing();
        setIsDeleteModalOpen(false);
        setIsDeactivatedModalOpen(false);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-team-member/${tableRecord?.id}`);
          }}
        >
          Edit
        </Button>
      ),
    },
    {
      key: "2",
      disabled: tableRecord?.status === "deactivated",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/password-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            setIsChangePswModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
          disabled={tableRecord?.status === "deactivated"}
        >
          Change password
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusItems = [
    {
      key: "header",
      label: (
        <div
          style={{
            padding: "6px 12px",
            color: "#474C4D",
            fontWeight: 500,
            fontSize: "12px",
            cursor: "default",
          }}
        >
          Change status to:
        </div>
      ),
      disabled: true,
    },
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("active");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />{" "}
          Active{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "deactivated",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("deactivated");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#6C7478", borderRadius: "2px" }}
          />{" "}
          Deactivate{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const statusItemsInvited = [
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("active");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />{" "}
          Active{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "inactive",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("inactive");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#6C7478", borderRadius: "2px" }}
          />{" "}
          Deactivate{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const columns: TableColumnsType<UsersTableType> = [
    {
      title: "User name",
      dataIndex: "first_name",
      width: 216,
      fixed: "left",
      sorter: (a, b) => a?.first_name?.localeCompare(b?.first_name),
      render: (text, record, index) => {
        return (
          <div className="text_with_avatar">
            {record?.image_url ? (
              <div className="participant_avatar">
                <Avatar src={record?.image_url} />
              </div>
            ) : (
              <div className="participant_avatar">
                <Avatar
                  style={{ backgroundColor: avatarColor?.[index] ?? "#ccc" }}
                >
                  {getInitials(record?.first_name, record?.last_name)}
                </Avatar>
              </div>
            )}
            <div className="text_wrap">
              <Tooltip title={`${text} ${record?.last_name}`}>
                {`${text} ${record?.last_name}`}
              </Tooltip>
              {myProfileData?.id === record?.id && (
                <span style={{ color: "#98A3A9" }}> (me)</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "type",
      width: 155,
      sorter: (a, b) => a?.type?.localeCompare(b?.type),
      render: (text: keyof typeof ROLE_TYPE) => {
        let className = "";
        if (text === "client_manager") {
          className = "light_purple_tag";
        } else if (text === "super_admin") {
          className = "light_pink_tag";
        } else {
          className = "defaultTag";
        }
        return text ? (
          <Tag className={`defaultTag ${className}`}>{ROLE_TYPE[text]}</Tag>
        ) : (
          "-"
        );
      },
    },
    // {
    //   title: "Clients assigned",
    //   dataIndex: "company_size",
    //   width: 147,
    //   render: (text, record) =>
    //     text ? text : record?.type === "super_admin" ? "-" : "0",
    //   align: "right",
    // },
    {
      title: "Account email",
      dataIndex: "email",
      width: 250,
      render: (text) => text ?? "-",
    },
    {
      title: "Mobile phone",
      dataIndex: "phone",
      width: 173,
      render: (text) => (text ? <PhoneInput disabled value={text} /> : "-"),
    },
    {
      title: "User status",
      dataIndex: "status",
      width: 125,
      render: (text, record) =>
        myProfileData?.id === record?.id ? (
          <Tag
            style={{
              background: "#00A07D",
              color: " #fff",
              textTransform: "capitalize",
              border: 0,
            }}
          >
            {text}
          </Tag>
        ) : (
          <Dropdown
            menu={{
              items: text === "invited" ? statusItemsInvited : statusItems,
              selectable: true,
              selectedKeys: [text],
            }}
            trigger={["click"]}
            overlayClassName="status_dropdown_overlay no-hover-color"
          >
            <Button
              onClick={(e) => {
                setTableRecord(record);
                e.stopPropagation();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className={`status_dropdown ${text}_status`}
              ref={dropdownRef}
            >
              <span style={{ textTransform: "capitalize" }}>{text}</span>{" "}
              <div className="icon_arrow">
                <CaretDownOutlined />
              </div>
            </Button>
          </Dropdown>
        ),
    },
    {
      title: "Last activity",
      dataIndex: "last_user_activity",
      width: 173,
      render: (text) => (text ? formatRelativeTime(text) : "-"),
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{
            items:
              myProfileData?.id === record?.id ? items?.slice(0, 2) : items,
          }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    const payload = {
      type: filters?.position
        ? [filters?.position]
        : ["client_manager", "super_admin"],
      status: filters?.status ? [filters?.status] : ["active"],
      search_text: filters?.search_text,
      offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
      limit: limit,
    };
    dispatch(getUsersListing(payload));
  }, [dispatch, filters, pagination, limit]);

  const deleteFunction = () => {
    setLoading(true);
    deleteUser(
      selectedRowKeys?.length > 0 ? selectedRowKeys : [tableRecord?.id || ""]
    )
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setIsAllDeleteModalOpen(false);
        setSelectedRowKeys([]);
        fetchUserListing();
        SuccessMessage("Team member deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const confirmDeletePopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete{" "}
            <b>
              {tableRecord?.first_name} {tableRecord?.last_name}
            </b>
            ?<br /> This action can't be undone.
          </>
        }
        btnText="Delete user"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const changePasswordPopup = {
    visibility: isChangePswModalOpen,
    content: (
      <ChangePasswordPopUp
        onClose={() => setIsChangePswModalOpen(false)}
        id={tableRecord?.id || ""}
      />
    ),
    width: 640,
    onCancel: () => setIsChangePswModalOpen(false),
  };

  const confirmDeactivatedPopUp = {
    visibility: isDeactivatedModalOpen,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setIsDeactivatedModalOpen(false)}
        heading="Deactivate user"
        paragraph={
          <>
            Are you sure you want to deactivate{" "}
            <b>
              {tableRecord?.first_name} {tableRecord?.last_name}
            </b>
            ?<br /> This user won't be able to access their account anymore. The
            data associated with this user (e.g., work history) will be
            preserved.
          </>
        }
        btnText="Deactivate user"
        btnApi={() => functionChangeStatus("deactivated")}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeactivatedModalOpen(false),
  };

  useEffect(() => {
    dispatch(removeUsersListingData());
  }, [dispatch]);

  const deleteAllUsersPopUp = {
    visibility: isAllDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsAllDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete <b>all users</b>
            ?<br /> This action can't be undone.
          </>
        }
        btnText="Delete user"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsAllDeleteModalOpen(false),
  };

  // Ensure current page is within available range after data changes (e.g., archiving/deleting items)
  useEffect(() => {
    const count = userListing?.count ?? 0;
    const totalPages = Math.max(1, Math.ceil(count / limit));
    if (pagination > totalPages) {
      setPagination(totalPages);
    }
  }, [userListing?.count, limit, pagination]);

  return (
    <DashboardTemplate loading={userListing?.loader || loading}>
      <Header title="Admin team" description="LandScaling operations team." />
      <TableUi<UsersTableType>
        columns={columns}
        data={{
          count: userListing?.count,
          data: userListing?.data || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            deactivated={deactivated}
            isDeactivated={isDeactivated}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonText="Add team member"
        createButtonLink="/add-team-member"
      />
      {userListing?.count === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              deactivated
                ? "There are no deactivated users"
                : filters?.search_text ||
                  filters?.company ||
                  filters?.position ||
                  filters?.language ||
                  filters?.status
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} users`
            }
            description={
              deactivated
                ? "Once the user is deactivated, they will appear in this list."
                : filters?.search_text ||
                  filters?.company ||
                  filters?.position ||
                  filters?.language ||
                  filters?.status
                ? "Try adjusting the filters and search"
                : "We designed this state but we think nobody will ever see it."
            }
            image={
              deactivated
                ? "/images/empty-icons/no-active.svg"
                : filters?.search_text ||
                  filters?.company ||
                  filters?.position ||
                  filters?.language ||
                  filters?.status
                ? "/images/empty-icons/no-active.svg"
                : "/images/empty-icons/no-archived.svg"
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar" style={{ minWidth: "235px" }}>
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "user" : "users"}`} selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsAllDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...confirmDeletePopUp} />
      <PopUp {...deleteAllUsersPopUp} />
      <PopUp {...changePasswordPopup} />
      <PopUp {...confirmDeactivatedPopUp} />
    </DashboardTemplate>
  );
}
